import axios, {AxiosResponse} from "axios";
import {
    Indicator,
    Node as SomatNode,
    NodeType,
    SurveySession, SurveySessionNodeProgress,
    SurveySessionProgressSummary, SurveySessionProgressTree
} from "@/types/models";
export const getChildren = async (node: SomatNode, session?: SurveySession) => {
    return await axios
        .get(route("api.session.children", {
            node: node,
            session: session?.id,
        }))
        .then((response: AxiosResponse<SomatNode[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const loadNodePath = async (nodeId: number, session?: SurveySession) => {
    return await axios
        .get(route("api.session.node-path", {
            node: nodeId,
            session: session?.id,
        }))
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const createArrayNode = async(node: SomatNode, session: SurveySession) => {
    return await axios
        .post(route("api.session.array-node.add", {
            node: node,
            session: session?.id,
        }))
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const deleteArrayNode = async(node: SomatNode, session: SurveySession) => {
    return await axios
        .delete(route("api.session.array-node.delete", {
            node: node,
            session: session?.id,
        }))
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getNodeTypes = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.node-types", {
            session: session?.id,
        }))
        .then((response: AxiosResponse<NodeType[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getProgressSummary = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.progress", {
            session: session?.id
        }))
        .then((response: AxiosResponse<SurveySessionProgressSummary>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getProgressTree = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.progress-tree", {
            session: session?.id
        }))
        .then((response: AxiosResponse<SurveySessionProgressTree>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`api.session.progress-tree call failed: ${error}`)
            throw error
        })
}

export const getProgressDetail = async(session: SurveySession, node?: SomatNode) => {
    const args: { session: number, detail: boolean, node?: number} = {
        session: session.id,
        detail: true
    }
    if(node) args.node = node.id
    return await axios
        .get(route("api.session.progress", args))
        .then((response: AxiosResponse<SurveySessionNodeProgress>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

/**
 * Where a reader sent to a section should land: the section itself where it asks something, and otherwise the
 * first section inside it that does. A section that only holds others, as a sector holds the phases of a flight,
 * has nothing of its own to show.
 *
 * @param evaluating Whether the session is being evaluated, when the evaluator's own questions are shown.
 */
export const firstSectionWithin = (node: SomatNode, evaluating: boolean = false): SomatNode => {
    // Whether there is a question to be seen in it, or in a group of questions drawn inside it: a hidden question,
    // or one that is only the evaluator's while the session is being answered, is nothing to land on. Content that has not
    // been fetched yet may hold one, so a section at the end of a branch is given the benefit of the doubt.
    const seen = (indicator: Indicator) => !indicator.hidden && (evaluating || !indicator.evaluation_only)

    const asks = (section: SomatNode): boolean => {
        if(section.content === undefined) return !!section.indicators?.some(seen) || !section.children?.length

        return section.content.some(item =>
            (item._class === 'Indicator' && seen(item as Indicator))
            || (item._class === 'Node' && asks(item as SomatNode))
        )
    }

    const find = (section: SomatNode): SomatNode | undefined => {
        if(asks(section)) return section
        for(const child of section.children ?? []) {
            const found = find(child)
            if(found) return found
        }
        return undefined
    }

    return find(node) ?? node
}

export const flattenHierarchy = (nodes: SomatNode[]): SomatNode[] => {
    return nodes.reduce((acc: SomatNode[], node: SomatNode) => {
        if(node.children) return acc.concat(node, flattenHierarchy(node.children))
        return acc.concat(node)
    }, [])
}


export const createNode = async(data: Omit<SomatNode, 'id'>) => {
    return await axios
        .post(route("api.node.create"), data)
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const updateNode = async(node: SomatNode) => {
    const { parent, children, content, ...data} = node
    return await axios
        .put(route("api.node.update"), data)
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const moveNode = async(
    node: SomatNode,
    relation: SomatNode,
    position: 'child' | 'sibling' | 'sibling-before' | 'inline-before' | 'inline-after',
    weight?: number
) => {
    const args: {
        id: number;
        relation_id: number;
        position: string;
        weight?: number;
    } = {
        id: node.id,
        relation_id: relation.id,
        position: position
    }

    if (weight !== undefined) {
        args.weight = weight;
    }

    return await axios
        .put(route("api.node.move"), args)
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const deleteNode = async(node: SomatNode) => {
    return await axios
        .delete(route("api.node.delete", {node: node.id}))
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}
